import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";

import { updatedDiff } from "deep-object-diff";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, DatePicker, Link, Modal, NumberInput, Text, YesNoRadioButton } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { BankCreditService, CreditService } from "services";
import { BankCreditResponse, CreditResponse } from "types";
import { UserUtils } from "utils";

import { ContractDetails } from "../../bank-selection/hooks/types";

const { REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID, REQUEST_PENDING } = ServiceConstants;
const { ROLE_CON, ROLE_CON_ADJ, ROLE_CON_ATT, ROLE_CON_ATT_OTHER, ROLE_CON_PAY } =
  AuthorityConstants;

const toNumber = (value: unknown) => (typeof value === "number" ? value : undefined);

const isSameValue = (a: unknown, b: unknown) =>
  a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : a === b;

interface CreditDetailsProps {
  credit: CreditResponse;
  bankCredit?: BankCreditResponse;
  readOnly?: boolean;
  // When set, the fields show and edit the approved bank's contract ("O kreditu")
  contract?: ContractDetails;
  onContractChange?: (updates: Partial<ContractDetails>) => void;
}

const CreditDetails = ({
  credit,
  bankCredit,
  readOnly = false,
  contract,
  onContractChange,
}: CreditDetailsProps) => {
  const { t } = useTranslation();

  const timeoutRefs = useRef<Map<string, any>>(new Map());
  const hydratingRef = useRef(false);

  // Same authorities that are required to see "O kreditu"
  const disabled = contract
    ? !UserUtils.hasAnyAuthority([ROLE_CON, ROLE_CON_PAY, ROLE_CON_ADJ, ROLE_CON_ATT, ROLE_CON_ATT_OTHER])
    : readOnly;

  useEffect(() => {
    setValues();
  }, [credit, bankCredit, contract]);

  const update = useCallback(async (propertyName: string, propertyValue: any) => {
    await CreditService.creditsUpdateProperty(credit.id, propertyName, propertyValue);
    timeoutRefs.current.delete(propertyName);
  }, [credit.id]);

  const updateCredit = useCallback(async (item: any) => {
    for (const propertyName in item) {
      if (timeoutRefs.current.has(propertyName)) {
        clearTimeout(timeoutRefs.current.get(propertyName));
      }
      timeoutRefs.current.set(
        propertyName,
        setTimeout(() => update(propertyName, item[propertyName]), 1000),
      );
    }
  }, [update]);

  const updateFirstInstallmentDate = useCallback(
    (value: Date | undefined) => {
      if (bankCredit) {
        BankCreditService.bankCreditUpdateMultipleProperties(bankCredit.id, {
          firstInstallmentDate: value,
        });
      } else {
        CreditService.creditsUpdateProperty(credit.id, "firstInstallmentDate", value);
      }
    },
    [bankCredit, credit.id],
  );

  const updateContract = useCallback(
    (changed: Record<string, any>, values: Record<string, any>) => {
      const updates: Partial<ContractDetails> = {};

      Object.keys(changed).forEach((key) => {
        const value = values[key];
        switch (key) {
          case "grantedCreditAmount":
            updates.creditAmount = toNumber(value);
            break;
          case "grantedRepaymentMonths":
            updates.paymentPeriod = toNumber(value);
            break;
          case "grantedInstallmentAmount":
            updates.monthlyInstallment = toNumber(value);
            break;
          case "securedLoanInstallment":
            updates.creditInsurance = value;
            if (!value) {
              updates.theTotalAmountOfTheLoanWithInsurance = undefined;
            }
            break;
          case "firstInstallmentDate":
            updateFirstInstallmentDate(value);
            break;
        }
      });

      if (Object.keys(updates).length > 0) {
        onContractChange?.(updates);
      }
    },
    [onContractChange, updateFirstInstallmentDate],
  );

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {},
    onValuesChange(values, previous) {
      if (disabled || hydratingRef.current) {
        return;
      }
      const changed = updatedDiff(previous, values);
      if (contract) {
        updateContract(changed, values);
        return;
      }
      updateCredit(changed);
    },
  });

  const setValues = useCallback(() => {
    const firstInstallmentDate = bankCredit?.firstInstallmentDate ?? credit.firstInstallmentDate;

    if (contract) {
      const values: Record<string, any> = {
        grantedCreditAmount: contract.creditAmount,
        grantedRepaymentMonths: contract.paymentPeriod,
        grantedInstallmentAmount: contract.monthlyInstallment,
        firstInstallmentDate: firstInstallmentDate ? new Date(firstInstallmentDate) : undefined,
        securedLoanInstallment: contract.creditInsurance,
      };
      const current: Record<string, any> = form.getValues();
      // Skip when nothing changed, so a refresh after saving doesn't reset the focused input
      if (Object.keys(values).every((key) => isSameValue(current[key], values[key]))) {
        return;
      }

      // Loading values is not a user change, don't save it back
      hydratingRef.current = true;
      form.setValues(values);
      hydratingRef.current = false;
      return;
    }

    form.setValues({
      grantedCreditAmount:
        bankCredit?.grantedCreditAmount ?? bankCredit?.creditAmount ?? credit.grantedCreditAmount,
      grantedRepaymentMonths:
        bankCredit?.grantedRepaymentMonths ?? bankCredit?.paymentPeriod ?? credit.grantedRepaymentMonths,
      grantedInstallmentAmount:
        bankCredit?.grantedInstallmentAmount ??
        bankCredit?.monthlyInstallmentFrom ??
        bankCredit?.monthlyInstallmentTo ??
        credit.grantedInstallmentAmount,
      firstInstallmentDate: firstInstallmentDate ? new Date(firstInstallmentDate) : undefined,
      securedLoanInstallment:
        bankCredit?.securedLoanInstallment ??
        bankCredit?.creditInsurance ??
        credit.securedLoanInstallment,
      postponedInstallments: credit.postponedInstallments,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCredit, credit, contract]);

  return (
    <Box>
      <SimpleGrid cols={4}>
        <NumberInput
          label={t("credit.grantedCreditAmount.label")}
          key={form.key("grantedCreditAmount")}
          {...form.getInputProps("grantedCreditAmount")}
          disabled={disabled}
        />
        <NumberInput
          label={t("credit.grantedRepaymentMonths.label")}
          key={form.key("grantedRepaymentMonths")}
          {...form.getInputProps("grantedRepaymentMonths")}
          plain={true}
          disabled={disabled}
        />
        <NumberInput
          label={t("credit.grantedInstallmentAmount.label")}
          key={form.key("grantedInstallmentAmount")}
          {...form.getInputProps("grantedInstallmentAmount")}
          allowDecimal={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled={disabled}
        />
        <DatePicker
          label={t("credit.firstInstallmentDate.label")}
          key={form.key("firstInstallmentDate")}
          {...form.getInputProps("firstInstallmentDate")}
          disabled={disabled}
        />
      </SimpleGrid>
      <SimpleGrid cols={4} mt={"md"}>
        <YesNoRadioButton
          label={t("credit.securedLoanInstallment.label")}
          name={"securedLoanInstallment"}
          form={form}
          disabled={disabled}
        />
      </SimpleGrid>
      {!readOnly && !contract && (
        <SimpleGrid cols={4} mt={"sm"}>
          <PostponeInstallment credit={credit} form={form} />
        </SimpleGrid>
      )}
    </Box>
  );
};

interface IPostponeInstallment {
  credit: CreditResponse;
  form: UseFormReturnType<any, any>;
}

const PostponeInstallment = ({ credit, form }: IPostponeInstallment) => {
  const { t } = useTranslation();

  const POSTPONING_MAX_NUMBER = 2;
  const postponeInstallmentServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID],
  );
  const postponedInstallments = form.getTransformedValues().postponedInstallments ?? 0;
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const postponeInstallment = useCallback(async () => {
    if (postponedInstallments >= 2) {
      return;
    }
    await CreditService.creditPostponeInstalment(credit.id);
    form.setFieldValue("postponedInstallments", postponedInstallments + 1);
    setModalVisible(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postponedInstallments, credit.id]);

  const isLoading = useMemo(
    () => postponeInstallmentServiceObject?.status === REQUEST_PENDING,
    [postponeInstallmentServiceObject],
  );

  return (
    <>
      <Box>
        <Button
          variant={"outline"}
          onClick={() => setModalVisible(true)}
          loading={isLoading}
          disabled={isLoading || postponedInstallments >= 2}
          mt={20}
          fullWidth={true}
        >
          {t("credit.postponeInstallment.button")}
        </Button>
        <Text size={"sm"} c={"var(--mantine-color-gray-7)"} ta={"center"}>
          {postponedInstallments >= POSTPONING_MAX_NUMBER
            ? t("credit.postponeInstallment.limit", { max: POSTPONING_MAX_NUMBER })
            : t("credit.postponeInstallment.info", {
                numberOf: postponedInstallments,
                max: POSTPONING_MAX_NUMBER,
              })}
        </Text>
      </Box>
      <Modal opened={modalVisible} onClose={() => setModalVisible(false)} withCloseButton={false}>
        <Text ta={"center"} mt={24} mb={12} style={{ fontWeight: "bold" }}>
          {t("credit.postponeInstallment.modal.title")}
        </Text>
        <Text ta={"center"} mb={24}>
          {t("credit.postponeInstallment.modal.description")}
        </Text>
        <Flex mt={32} mb={12} justify={"space-between"} align={"center"} px={32}>
          <Link
            onClick={() => setModalVisible(false)}
            disabled={isLoading}
            c={"var(--mantine-color-gray-7)"}
          >
            {t("credit.postponeInstallment.modal.cancel")}
          </Link>
          <Button onClick={postponeInstallment} disabled={isLoading}>
            <Text>{t("credit.postponeInstallment.modal.submit")}</Text>
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default CreditDetails;
